"use client";

/**
 * Migas de pan: los últimos pasos del usuario antes de que algo explote.
 *
 * Para un `Minified React error #185` ("Maximum update depth exceeded") el stack
 * NO sirve —lo tira el planificador de React, así que apunta a sus internos y no
 * al componente culpable—. Lo que sí resuelve el caso es saber **qué tocó** justo
 * antes. "Tocó la foto de Sentadilla Búlgara, después Cambiar ejercicio, después
 * eligió Hip Thrust" vale más que veinte líneas de `chunk-4f2a.js`.
 *
 * Es un anillo de tamaño fijo en memoria: no persiste, no viaja a ningún lado y
 * sólo se lee cuando la pantalla de error arma su detalle copiable.
 *
 * Se guarda el TEXTO VISIBLE de lo que se tocó, nunca el valor de los campos:
 * las notas de una sesión o el mail de alguien no tienen por qué terminar en un
 * reporte que después se pega en un chat.
 */

const MAX_BREADCRUMBS = 25;

interface Breadcrumb {
  /** Milisegundos desde que cargó la página. */
  t: number;
  tipo: "click" | "ruta" | "consola" | "rechazo";
  detalle: string;
}

const breadcrumbs: Breadcrumb[] = [];
const inicio = typeof performance !== "undefined" ? performance.now() : 0;

function ahora() {
  return typeof performance !== "undefined" ? Math.round(performance.now() - inicio) : 0;
}

export function addBreadcrumb(tipo: Breadcrumb["tipo"], detalle: string) {
  const limpio = detalle.replace(/\s+/g, " ").trim().slice(0, 90);
  if (!limpio) return;
  breadcrumbs.push({ t: ahora(), tipo, detalle: limpio });
  if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
}

/** Las migas, de la más vieja a la más nueva, con el tiempo relativo. */
export function formatBreadcrumbs(): string | null {
  if (breadcrumbs.length === 0) return null;
  return breadcrumbs
    .map((b) => `  ${(b.t / 1000).toFixed(1)}s [${b.tipo}] ${b.detalle}`)
    .join("\n");
}

/**
 * Nombre legible de lo que se tocó. Se prefiere el `aria-label` porque en esta
 * app describe la acción completa ("Cambiar ejercicio de Press Plano BR"),
 * mientras que el texto visible de un botón suele ser un ícono solo.
 */
function describirElemento(el: Element | null): string {
  if (!el) return "(nada)";
  const accionable = el.closest("button, a, [role='button'], [role='radio'], [role='option']");
  const objetivo = accionable ?? el;
  const aria = objetivo.getAttribute("aria-label");
  if (aria) return aria;
  const texto = (objetivo as HTMLElement).innerText ?? objetivo.textContent ?? "";
  if (texto.trim()) return texto;
  const img = objetivo.querySelector?.("img");
  if (img?.getAttribute("alt")) return `imagen: ${img.getAttribute("alt")}`;
  return `<${objetivo.tagName.toLowerCase()}>`;
}

/** Contexto: ¿estaba en un modal? ¿en qué bloque de la sesión? */
function describirContexto(el: Element | null): string {
  if (!el) return "";
  if (el.closest("[role='dialog']")) return " (en un modal)";
  const bloque = el.closest("[id^='live-block-']");
  if (bloque) return " (en un bloque de la sesión)";
  return "";
}

let instalado = false;

/**
 * Engancha los listeners UNA sola vez. Todo va en `capture` y `passive` para no
 * cambiar en nada el comportamiento de la app: esto observa, no participa.
 */
export function installBreadcrumbs() {
  if (instalado || typeof window === "undefined") return;
  instalado = true;

  window.addEventListener(
    "click",
    (event) => {
      const el = event.target instanceof Element ? event.target : null;
      addBreadcrumb("click", `${describirElemento(el)}${describirContexto(el)}`);
    },
    { capture: true, passive: true },
  );

  // Cambios de vista sin recarga: en la sesión, el paso vive en la URL.
  let ultimaUrl = window.location.href;
  const revisarUrl = () => {
    if (window.location.href === ultimaUrl) return;
    ultimaUrl = window.location.href;
    addBreadcrumb("ruta", ultimaUrl.replace(window.location.origin, ""));
  };
  window.addEventListener("popstate", revisarUrl, { passive: true });
  window.setInterval(revisarUrl, 1000);

  window.addEventListener(
    "unhandledrejection",
    (event) => {
      const razon = event.reason;
      addBreadcrumb("rechazo", razon instanceof Error ? razon.message : String(razon));
    },
    { passive: true },
  );

  // Los `console.error` previos suelen traer el aviso que React imprime ANTES
  // de tirar. Se envuelve sin reemplazar: el original se sigue llamando.
  const original = console.error;
  console.error = (...args: unknown[]) => {
    addBreadcrumb(
      "consola",
      args
        .map((a) => (a instanceof Error ? a.message : String(a)))
        .join(" ")
        .slice(0, 120),
    );
    original(...args);
  };

  // En desarrollo se puede leer el rastro sin esperar a que algo explote.
  if (process.env.NODE_ENV !== "production") {
    (window as unknown as Record<string, unknown>).__breadcrumbs = formatBreadcrumbs;
  }
}
